import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Menu, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Logo } from "@/components/Logo";
import { CONTACT } from "@/constants/contact";

const navLinkClass =
  "nav-underline text-sm font-medium text-muted-foreground transition-colors hover:text-foreground";

const garageLinks = [
  { to: "/#garage", label: "Le garage", testid: "nav-garage-link" },
  { to: "/#engagements", label: "Nos engagements", testid: "nav-engagements-link" },
];

const vehicleLinks = [
  { to: "/vehicule-de-pret", label: "Véhicule de prêt", testid: "nav-vehicule-pret-link" },
  { to: "/location-de-vehicule", label: "Location de véhicule", testid: "nav-location-link" },
  { to: "/convoyage", label: "Convoyage auto", testid: "nav-convoyage-link" },
];

const topLinks = [
  { to: "/", label: "Accueil", testid: "nav-home-link" },
  { to: "/#prestations", label: "Prestations", testid: "nav-prestations-link" },
  { to: "/occasions", label: "Occasions", testid: "nav-occasions-link" },
  { to: "/depot-vente", label: "Dépôt-vente", testid: "nav-depot-vente-link" },
  { to: "/pneus", label: "Pneus", testid: "nav-pneus-link" },
];

const contactLink = { to: "/#contact", label: "Contact", testid: "nav-contact-link" };

export const Header = () => {
  const [open, setOpen] = useState(false);

  const desktopLink = (link) => (
    <Link key={link.to} to={link.to} data-testid={link.testid} className={navLinkClass}>
      {link.label}
    </Link>
  );

  const mobileLink = (link) => (
    <Link
      key={link.to}
      to={link.to}
      data-testid={`mobile-${link.testid}`}
      onClick={() => setOpen(false)}
      className="text-base font-medium text-foreground"
    >
      {link.label}
    </Link>
  );

  const mobileSubLink = (link) => (
    <Link
      key={link.to}
      to={link.to}
      data-testid={`mobile-${link.testid}`}
      onClick={() => setOpen(false)}
      className="pl-3 text-base font-medium text-foreground"
    >
      {link.label}
    </Link>
  );

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 border-b border-border bg-background/90 backdrop-blur-md"
    >
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4">
        <Link to="/" aria-label="Accueil MECA AUTO CLEAN 01">
          <Logo compact />
        </Link>

        <nav className="hidden items-center gap-5 lg:flex" aria-label="Navigation principale">
          {desktopLink(topLinks[0])}
          {desktopLink(topLinks[1])}
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="nav-garage-dropdown"
              className={`${navLinkClass} flex items-center gap-1 outline-none`}
            >
              Le garage <ChevronDown className="h-3.5 w-3.5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {garageLinks.map((l) => (
                <DropdownMenuItem key={l.to} asChild>
                  <Link to={l.to} data-testid={l.testid}>
                    {l.label}
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          {topLinks.slice(2).map(desktopLink)}
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="nav-vehicules-dropdown"
              className={`${navLinkClass} flex items-center gap-1 outline-none`}
            >
              Véhicules <ChevronDown className="h-3.5 w-3.5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {vehicleLinks.map((l) => (
                <DropdownMenuItem key={l.to} asChild>
                  <Link to={l.to} data-testid={l.testid}>
                    {l.label}
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          {desktopLink(contactLink)}
          <Button asChild size="sm" data-testid="nav-devis-button">
            <Link to="/#contact">Devis gratuit</Link>
          </Button>
        </nav>

        <div className="lg:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                data-testid="mobile-menu-button"
                aria-label="Ouvrir le menu"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" data-testid="mobile-menu" className="w-72 overflow-y-auto">
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <div className="mt-6 flex flex-col gap-4">
                {topLinks.slice(0, 2).map(mobileLink)}
                <p className="mt-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Le garage
                </p>
                {garageLinks.map(mobileSubLink)}
                {topLinks.slice(2).map(mobileLink)}
                <p className="mt-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Véhicules
                </p>
                {vehicleLinks.map(mobileSubLink)}
                {mobileLink(contactLink)}
                <a
                  href={CONTACT.phoneHref}
                  data-testid="mobile-call-link"
                  className="mt-4 flex items-center gap-2 text-sm font-semibold text-primary"
                >
                  <Phone className="h-4 w-4" /> {CONTACT.phoneDisplay}
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
